/**
 * NW Shop Page — Filter / Sort / Search / URL State System
 * Converted from nw-shop-page-v3.html for Shopify
 *
 * Data is injected from the Liquid section via:
 *   window.NWShopData = { products: [...] }
 */
const NWShop = (() => {
  'use strict';

  // ──────────────────────────────────────────────
  // DATA — Injected from Liquid
  // ──────────────────────────────────────────────
  const SHOP_DATA = window.NWShopData || {};
  const PRODUCTS = SHOP_DATA.products || [];
  const INJECTED_CATEGORIES = SHOP_DATA.categories || [];
  const INJECTED_TYPES = SHOP_DATA.types || [];
  const COLLECTION_HANDLE = SHOP_DATA.collectionHandle || '';
  const TOTAL_PRODUCTS = SHOP_DATA.totalProducts || 0;
  const TOTAL_PAGES = SHOP_DATA.totalPages || 1;
  const SHOW_SALE_BADGES = SHOP_DATA.showSaleBadges !== false;
  const SHOW_COMPARE_AT_PRICES = SHOP_DATA.showCompareAtPrices !== false;
  const IS_BEST_SELLERS_COLLECTION = SHOP_DATA.isBestSellersCollection === true;
  const parsedCollectionProductLimit = Number(SHOP_DATA.collectionProductLimit);
  const COLLECTION_PRODUCT_LIMIT = Number.isFinite(parsedCollectionProductLimit) && parsedCollectionProductLimit > 0
    ? Math.floor(parsedCollectionProductLimit)
    : null;
  const VIEW_RANKINGS_VIEW = SHOP_DATA.viewRankingsView || 'nw-rankings';
  const BEST_SELLER_LABEL = (SHOP_DATA.labels && SHOP_DATA.labels.bestSeller) || '';
  const MOST_POPULAR_LABEL = (SHOP_DATA.labels && SHOP_DATA.labels.mostPopular) || '';
  let bestSellingProductIds = [];
  let bestSellerProductIds = new Set();
  let mostPopularProductIds = new Set();
  let productViewCounts = new Map();
  let bestSellersReady = !IS_BEST_SELLERS_COLLECTION;

  function normalizeToSlug(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[+]/g, ' plus ')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/-and-/g, '-')   // "supplies-and-accessories" → "supplies-accessories"
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function parseFilterValues(rawValue) {
    if (Array.isArray(rawValue)) {
      return rawValue.map((v) => String(v || '').trim()).filter(Boolean);
    }

    if (rawValue === null || rawValue === undefined) return [];

    if (typeof rawValue === 'string') {
      return rawValue
        .split(/[,|]/)
        .map((v) => v.trim())
        .filter(Boolean);
    }

    const single = String(rawValue).trim();
    return single ? [single] : [];
  }

  function toFilterEntries(rawValue) {
    const seen = new Set();
    const entries = [];

    parseFilterValues(rawValue).forEach((name) => {
      const slug = normalizeToSlug(name);
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      entries.push({ slug, name });
    });

    return entries;
  }

  function getProductSizeEntries(product) {
    const raw = parseFilterValues(product.filterSizeMeta);
    const seen = new Set();
    const entries = [];

    raw.forEach((name) => {
      let formattedName = name;
      const match = name.match(/^(\d+)\s*-?\s*persons?$/i);
      if (match) {
        formattedName = match[1] + ' Person';
      }
      const slug = normalizeToSlug(formattedName);
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      entries.push({ slug, name: formattedName });
    });

    return entries;
  }

  function getProductTypeEntries(product) {
    const metaEntries = toFilterEntries(product.filterTypeMeta);
    if (metaEntries.length) return metaEntries;

    const fallbackType = product.typeName || product.type;
    return fallbackType ? toFilterEntries(fallbackType) : [];
  }

  function getProductBrandEntries(product) {
    return toFilterEntries(product.filterBrandMeta);
  }

  function getProductFeatureEntries(product) {
    return toFilterEntries(product.filterFeaturesMeta);
  }

  function getProductCircuitEntries(product) {
    return toFilterEntries(product.filterCircuitsMeta);
  }

  function getProductFootprintEntries(product) {
    return toFilterEntries(product.filterFootprintMeta);
  }

  function getProductAddedFeatureEntries(product) {
    return toFilterEntries(product.filterAddedFeaturesMeta);
  }

  function sortOptionsWithPreference(options, preferredSlugs) {
    const preferredIndex = new Map(preferredSlugs.map((slug, index) => [slug, index]));

    return [...options].sort((a, b) => {
      const ai = preferredIndex.has(a.slug) ? preferredIndex.get(a.slug) : Number.MAX_SAFE_INTEGER;
      const bi = preferredIndex.has(b.slug) ? preferredIndex.get(b.slug) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
  }

  function buildFilterOptions(getEntries, products = PRODUCTS) {
    const bySlug = new Map();

    products.forEach((product) => {
      getEntries(product).forEach((entry) => {
        const existing = bySlug.get(entry.slug);
        if (existing) {
          existing.count += 1;
        } else {
          bySlug.set(entry.slug, { ...entry, count: 1 });
        }
      });
    });

    return [...bySlug.values()];
  }

  function buildOptionsFromInjected(source) {
    const bySlug = new Map();

    source.forEach((item) => {
      const slug = normalizeToSlug(item.slug || item.name);
      if (!slug) return;

      const name = String(item.name || item.slug || '').trim();
      if (!name) return;

      const existing = bySlug.get(slug);
      if (existing) {
        existing.count += Number(item.count || 0);
      } else {
        bySlug.set(slug, { slug, name, count: Number(item.count || 0) });
      }
    });

    return [...bySlug.values()];
  }

  function buildCategoryOptions() {
    const bySlug = new Map();

    INJECTED_CATEGORIES.forEach((category) => {
      const slug = normalizeToSlug(category.slug || category.name);
      if (!slug) return;

      bySlug.set(slug, {
        slug,
        name: String(category.name || category.slug || '').trim(),
        count: 0,
      });
    });

    PRODUCTS.forEach((product) => {
      const productCategorySlugs = Array.isArray(product.categorySlugs)
        ? product.categorySlugs.map((slug) => normalizeToSlug(slug)).filter(Boolean)
        : [];

      productCategorySlugs.forEach((slug) => {
        const existing = bySlug.get(slug);
        if (existing) {
          existing.count += 1;
        }
      });
    });

    return [...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  function buildTypeOptions(products = PRODUCTS) {
    const fromMeta = sortOptionsWithPreference(buildFilterOptions(getProductTypeEntries, products), [
      'traditional',
      'infrared',
      'integrated-therapy',
    ]);

    // The injected list counts the whole collection, so it only stands in when unscoped.
    if (fromMeta.length || products !== PRODUCTS) return fromMeta;

    return sortOptionsWithPreference(buildOptionsFromInjected(INJECTED_TYPES), [
      'traditional',
      'infrared',
      'integrated-therapy',
    ]);
  }

  const CATEGORY_OPTIONS = buildCategoryOptions();

  // Size / type / brand / feature / ... options are built from the products in the
  // selected categories (all products when none is selected), so a mega-menu link such
  // as /collections/huum?category=electric-heaters only offers filters for those heaters.
  let SIZE_OPTIONS = [];
  let TYPE_OPTIONS = [];
  let BRAND_OPTIONS = [];
  let FEATURE_OPTIONS = [];
  let CIRCUIT_OPTIONS = [];
  let FOOTPRINT_OPTIONS = [];
  let ADDED_FEATURE_OPTIONS = [];

  function buildFacetOptions(products) {
    SIZE_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductSizeEntries, products), [
      '1-person',
      '2-person',
      '3-person',
      '4-person',
      '5-person',
      '6-person',
    ]);

    TYPE_OPTIONS = buildTypeOptions(products);

    BRAND_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductBrandEntries, products), [
      'leil',
      'saunalife',
      'golden-designs',
      'signature-saunas',
      'maxxus',
      'dynamic',
    ]);

    FEATURE_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductFeatureEntries, products), []);

    CIRCUIT_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductCircuitEntries, products), []);

    FOOTPRINT_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductFootprintEntries, products), []);

    ADDED_FEATURE_OPTIONS = sortOptionsWithPreference(buildFilterOptions(getProductAddedFeatureEntries, products), []);
  }

  buildFacetOptions(IS_BEST_SELLERS_COLLECTION ? [] : PRODUCTS);

  // ──────────────────────────────────────────────
  // STATE
  // ──────────────────────────────────────────────
  let state = {
    selectedCategories: [],
    selectedSizes: [],
    selectedTypes: [],
    selectedBrands: [],
    selectedFeatures: [],
    selectedCircuits: [],
    selectedFootprints: [],
    selectedAddedFeatures: [],
    minPrice: null,
    maxPrice: null,
    searchTerm: '',
    sortBy: '',
  };

  let searchTimeout = null;

  // ──────────────────────────────────────────────
  // DOM HELPERS
  // ──────────────────────────────────────────────
  const $ = (sel) => document.querySelector(sel);
  const $$ = (sel) => document.querySelectorAll(sel);
  const $id = (id) => document.getElementById(id);

  // ──────────────────────────────────────────────
  // INIT
  // ──────────────────────────────────────────────
  function init() {
    // Bail if no shop section on page
    if (!$id('nwProductsGrid')) return;

    buildSidebarCheckboxes();
    bindEvents();
    restoreFromURL();
    const rankingsPromise = fetchCollectionRankings();
    const remainingProductsPromise = fetchRemainingProducts();

    if (IS_BEST_SELLERS_COLLECTION) {
      renderProducts();
      updateUI();
      Promise.all([rankingsPromise, remainingProductsPromise]).finally(() => {
        bestSellersReady = true;
        if (IS_BEST_SELLERS_COLLECTION) {
          buildFacetOptions(getCollectionProducts());
          renderFacetGroups();
        }
        renderProducts();
        updateUI();
      });
    } else {
      renderProducts();
      updateUI();
      rankingsPromise.finally(() => {
        renderProducts();
        updateUI();
      });
    }

    window.addEventListener('resize', alignPriceRatingRowHeights);
  }

  // ──────────────────────────────────────────────
  // EVENT BINDINGS
  // ──────────────────────────────────────────────
  function bindEvents() {
    // Min Price
    const minPriceEl = $id('nwPriceMin');
    if (minPriceEl) {
      minPriceEl.addEventListener('input', () => {
        state.minPrice = minPriceEl.value ? parseFloat(minPriceEl.value) : null;
        renderProducts();
        updateUI();
      });
    }

    // Max Price
    const maxPriceEl = $id('nwPriceMax');
    if (maxPriceEl) {
      maxPriceEl.addEventListener('input', () => {
        state.maxPrice = maxPriceEl.value ? parseFloat(maxPriceEl.value) : null;
        renderProducts();
        updateUI();
      });
    }

    // Sort
    const sortEl = $id('nw-sort');
    if (sortEl) {
      sortEl.addEventListener('change', () => {
        state.sortBy = sortEl.value;
        renderProducts();
      });
    }

    // Search (debounced)
    const searchEl = $id('nw-search');
    if (searchEl) {
      searchEl.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          state.searchTerm = searchEl.value.trim();
          renderProducts();
          updateUI();
        }, 300);
      });

      searchEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          clearTimeout(searchTimeout);
          state.searchTerm = searchEl.value.trim();
          renderProducts();
          updateUI();
        }
      });
    }

    // Search button click
    const searchBtn = $('.nw-search-btn');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        clearTimeout(searchTimeout);
        state.searchTerm = $id('nw-search').value.trim();
        renderProducts();
        updateUI();
      });
    }

    // Filter button → open sidebar
    const refineBtn = $id('nwRefineBtn');
    if (refineBtn) {
      refineBtn.addEventListener('click', openSidebar);
    }

    // Sidebar close + apply
    const closeBtn = $id('nwCloseBtn');
    if (closeBtn) closeBtn.addEventListener('click', closeSidebar);

    const overlay = $id('nwSidebarOverlay');
    if (overlay) overlay.addEventListener('click', closeSidebar);

    const applyBtn = $id('nwApplyFilters');
    if (applyBtn) applyBtn.addEventListener('click', closeSidebar);

    // Clear all filters
    const clearBtn = $id('nw-clear-all-filters');
    if (clearBtn) clearBtn.addEventListener('click', clearAllFilters);

    // Keyboard: Escape to close sidebar
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeSidebar();
    });
  }

  // ──────────────────────────────────────────────
  // BUILD SIDEBAR CHECKBOXES (Size + Type + Brand)
  // ──────────────────────────────────────────────
  let categoryGroupVisible = false;

  function buildSidebarCheckboxes() {
    const isSpecificCollection = SHOP_DATA.isSpecificCollection === true;
    if (isSpecificCollection) {
      const catGroup = $id('nwSidebarGroupCategories');
      if (catGroup) catGroup.style.display = 'none';
    }

    categoryGroupVisible = !isSpecificCollection && renderFilterCheckboxGroup({
      container: $id('nwSidebarCategories'),
      options: CATEGORY_OPTIONS,
      idPrefix: 'nw-category',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedCategories, slug, checked);
        refreshFacetScope();
        renderProducts();
        updateUI();
      },
    });

    renderFacetGroups();
  }

  // Renders every group except Categories from the current *_OPTIONS lists.
  function renderFacetGroups() {
    const visibleGroupCount = [
      renderFilterCheckboxGroup({
      container: $id('nwSidebarSizes'),
      options: SIZE_OPTIONS,
      idPrefix: 'nw-size',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedSizes, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarTypes'),
      options: TYPE_OPTIONS,
      idPrefix: 'nw-type',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedTypes, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarBrands'),
      options: BRAND_OPTIONS,
      idPrefix: 'nw-brand',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedBrands, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarFeatures'),
      options: FEATURE_OPTIONS,
      idPrefix: 'nw-feature',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedFeatures, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarCircuits'),
      options: CIRCUIT_OPTIONS,
      idPrefix: 'nw-circuit',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedCircuits, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarFootprints'),
      options: FOOTPRINT_OPTIONS,
      idPrefix: 'nw-footprint',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedFootprints, slug, checked);
        renderProducts();
        updateUI();
      },
      }),

      renderFilterCheckboxGroup({
      container: $id('nwSidebarAddedFeatures'),
      options: ADDED_FEATURE_OPTIONS,
      idPrefix: 'nw-added-feature',
      onChange: (slug, checked) => {
        toggleSelection(state.selectedAddedFeatures, slug, checked);
        renderProducts();
        updateUI();
      },
      }),
    ].filter(Boolean).length;

    updateFilterPanelVisibility(visibleGroupCount + (categoryGroupVisible ? 1 : 0));
  }

  // Re-scopes the sidebar to the selected categories and drops selections that no
  // longer exist in that scope (they would otherwise hide every product).
  function refreshFacetScope() {
    buildFacetOptions(getCategoryScopeProducts());

    state.selectedSizes = sanitizeSelectedValues(state.selectedSizes, SIZE_OPTIONS);
    state.selectedTypes = sanitizeSelectedValues(state.selectedTypes, TYPE_OPTIONS);
    state.selectedBrands = sanitizeSelectedValues(state.selectedBrands, BRAND_OPTIONS);
    state.selectedFeatures = sanitizeSelectedValues(state.selectedFeatures, FEATURE_OPTIONS);
    state.selectedCircuits = sanitizeSelectedValues(state.selectedCircuits, CIRCUIT_OPTIONS);
    state.selectedFootprints = sanitizeSelectedValues(state.selectedFootprints, FOOTPRINT_OPTIONS);
    state.selectedAddedFeatures = sanitizeSelectedValues(state.selectedAddedFeatures, ADDED_FEATURE_OPTIONS);

    renderFacetGroups();
    syncSidebarCheckboxes();
  }

  function renderFilterCheckboxGroup({ container, options, idPrefix, onChange }) {
    if (!container) return false;

    const groupElement = container.closest('.nw-sidebar-group');
    if (!options.length) {
      container.innerHTML = '';
      if (groupElement) groupElement.style.display = 'none';
      return false;
    }

    if (groupElement) groupElement.style.display = '';

    container.innerHTML = options.map(option => `
        <div class="nw-sidebar-checkbox">
          <input type="checkbox" id="${idPrefix}-${option.slug}" value="${option.slug}">
          <label for="${idPrefix}-${option.slug}">${option.name} ${Number.isFinite(option.count) ? `(${option.count})` : ''}</label>
        </div>
      `).join('');

    container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => onChange(cb.value, cb.checked));
    });

    return true;
  }

  function updateFilterPanelVisibility(visibleGroupCount) {
    const refineBtn = $id('nwRefineBtn');
    const sidebar = $id('nwSidebar');
    const overlay = $id('nwSidebarOverlay');
    const shouldShow = visibleGroupCount > 0;

    if (refineBtn) refineBtn.style.display = shouldShow ? '' : 'none';
    if (sidebar) sidebar.style.display = shouldShow ? '' : 'none';
    if (overlay) overlay.style.display = shouldShow ? '' : 'none';
  }

  function toggleSelection(list, slug, checked) {
    if (checked) {
      if (!list.includes(slug)) list.push(slug);
    } else {
      const idx = list.indexOf(slug);
      if (idx > -1) list.splice(idx, 1);
    }
  }

  // ──────────────────────────────────────────────
  // SYNC SIDEBAR CHECKBOXES WITH STATE
  // ──────────────────────────────────────────────
  function syncSidebarCheckboxes() {
    syncCheckboxGroup('nwSidebarCategories', state.selectedCategories);
    syncCheckboxGroup('nwSidebarSizes', state.selectedSizes);
    syncCheckboxGroup('nwSidebarTypes', state.selectedTypes);
    syncCheckboxGroup('nwSidebarBrands', state.selectedBrands);
    syncCheckboxGroup('nwSidebarFeatures', state.selectedFeatures);
    syncCheckboxGroup('nwSidebarCircuits', state.selectedCircuits);
    syncCheckboxGroup('nwSidebarFootprints', state.selectedFootprints);
    syncCheckboxGroup('nwSidebarAddedFeatures', state.selectedAddedFeatures);
  }

  function syncCheckboxGroup(containerId, selectedValues) {
    const container = $id(containerId);
    if (!container) return;

    container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.checked = selectedValues.includes(cb.value);
    });
  }

  // ──────────────────────────────────────────────
  // SIDEBAR OPEN / CLOSE
  // ──────────────────────────────────────────────
  function openSidebar() {
    const overlay = $id('nwSidebarOverlay');
    const sidebar = $id('nwSidebar');
    if (overlay) overlay.classList.add('active');
    if (sidebar) sidebar.classList.add('active');
    if (window.innerWidth < 768) document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    const overlay = $id('nwSidebarOverlay');
    const sidebar = $id('nwSidebar');
    if (overlay) overlay.classList.remove('active');
    if (sidebar) sidebar.classList.remove('active');
    document.body.style.overflow = '';
  }

  // ──────────────────────────────────────────────
  // CLEAR FILTERS
  // ──────────────────────────────────────────────
  function clearAllFilters() {
    state.selectedCategories = [];
    state.selectedSizes = [];
    state.selectedTypes = [];
    state.selectedBrands = [];
    state.selectedFeatures = [];
    state.selectedCircuits = [];
    state.selectedFootprints = [];
    state.selectedAddedFeatures = [];
    state.minPrice = null;
    state.maxPrice = null;
    state.searchTerm = '';
    state.sortBy = '';
    const minPriceEl = $id('nwPriceMin');
    if (minPriceEl) minPriceEl.value = '';
    const maxPriceEl = $id('nwPriceMax');
    if (maxPriceEl) maxPriceEl.value = '';
    const searchEl = $id('nw-search');
    if (searchEl) searchEl.value = '';
    const sortEl = $id('nw-sort');
    if (sortEl) sortEl.value = '';
    refreshFacetScope();
    renderProducts();
    updateUI();
  }

  function removeFilterTag(type, value) {
    if (type === 'category') {
      state.selectedCategories = state.selectedCategories.filter((c) => c !== value);
    } else if (type === 'size') {
      state.selectedSizes = state.selectedSizes.filter(s => s !== value);
    } else if (type === 'type') {
      state.selectedTypes = state.selectedTypes.filter(t => t !== value);
    } else if (type === 'brand') {
      state.selectedBrands = state.selectedBrands.filter(b => b !== value);
    } else if (type === 'feature') {
      state.selectedFeatures = state.selectedFeatures.filter(f => f !== value);
    } else if (type === 'circuit') {
      state.selectedCircuits = state.selectedCircuits.filter(c => c !== value);
    } else if (type === 'footprint') {
      state.selectedFootprints = state.selectedFootprints.filter(f => f !== value);
    } else if (type === 'added-feature') {
      state.selectedAddedFeatures = state.selectedAddedFeatures.filter(f => f !== value);
    } else if (type === 'search') {
      state.searchTerm = '';
      const searchEl = $id('nw-search');
      if (searchEl) searchEl.value = '';
    }
    if (type === 'category') {
      refreshFacetScope();
    } else {
      syncSidebarCheckboxes();
    }
    renderProducts();
    updateUI();
  }

  // ──────────────────────────────────────────────
  // FUZZY SEARCH (client-side)
  // ──────────────────────────────────────────────
  function fuzzyMatch(text, query) {
    const lower = text.toLowerCase();
    const q = query.toLowerCase();
    if (lower.includes(q)) return true;
    let idx = 0;
    for (let i = 0; i < lower.length && idx < q.length; i++) {
      if (lower[i] === q[idx]) idx++;
    }
    return idx === q.length;
  }

  function escapeHTML(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ──────────────────────────────────────────────
  // FILTER + SORT
  // ──────────────────────────────────────────────
  function getEntrySlugs(entries) {
    return entries.map((entry) => entry.slug);
  }

  function productMatchesCategories(product, categories) {
    const categorySlugs = Array.isArray(product.categorySlugs)
      ? product.categorySlugs.map((slug) => normalizeToSlug(slug)).filter(Boolean)
      : [];

    return categories.some((slug) => categorySlugs.includes(slug));
  }

  // Products the sidebar options are built from: the selected categories, or everything.
  function getCategoryScopeProducts() {
    if (!state.selectedCategories.length) return PRODUCTS;
    return PRODUCTS.filter((p) => productMatchesCategories(p, state.selectedCategories));
  }

  function getCollectionProducts() {
    if (!IS_BEST_SELLERS_COLLECTION || !COLLECTION_PRODUCT_LIMIT) {
      return [...PRODUCTS];
    }

    if (!bestSellersReady) return [];

    if (bestSellingProductIds.length === 0) {
      return PRODUCTS.slice(0, COLLECTION_PRODUCT_LIMIT);
    }

    const productsById = new Map(PRODUCTS.map((product) => [String(product.productId), product]));
    return bestSellingProductIds
      .slice(0, COLLECTION_PRODUCT_LIMIT)
      .map((productId) => productsById.get(productId))
      .filter(Boolean);
  }

  function getProductViewCount(product) {
    return productViewCounts.get(String(product.productId)) || 0;
  }

  function getFilteredProducts() {
    let filtered = getCollectionProducts();

    // Filter by price
    if (state.minPrice !== null) {
      filtered = filtered.filter(p => p.price >= state.minPrice);
    }
    if (state.maxPrice !== null) {
      filtered = filtered.filter(p => p.price <= state.maxPrice);
    }

    // Filter by categories (mega-menu tag groups)
    if (state.selectedCategories.length > 0) {
      filtered = filtered.filter((p) => productMatchesCategories(p, state.selectedCategories));
    }

    // Filter by size (tag based)
    if (state.selectedSizes.length > 0) {
      filtered = filtered.filter(p => {
        const sizeSlugs = getEntrySlugs(getProductSizeEntries(p));
        return state.selectedSizes.some((slug) => sizeSlugs.includes(slug));
      });
    }

    // Filter by type
    if (state.selectedTypes.length > 0) {
      filtered = filtered.filter(p => {
        const typeSlugs = getEntrySlugs(getProductTypeEntries(p));
        return state.selectedTypes.some((slug) => typeSlugs.includes(slug));
      });
    }

    // Filter by brand
    if (state.selectedBrands.length > 0) {
      filtered = filtered.filter(p => {
        const brandSlugs = getEntrySlugs(getProductBrandEntries(p));
        return state.selectedBrands.some((slug) => brandSlugs.includes(slug));
      });
    }

    // Filter by features
    if (state.selectedFeatures.length > 0) {
      filtered = filtered.filter(p => {
        const featureSlugs = getEntrySlugs(getProductFeatureEntries(p));
        return state.selectedFeatures.some((slug) => featureSlugs.includes(slug));
      });
    }

    // Filter by circuits
    if (state.selectedCircuits.length > 0) {
      filtered = filtered.filter(p => {
        const circuitSlugs = getEntrySlugs(getProductCircuitEntries(p));
        return state.selectedCircuits.some((slug) => circuitSlugs.includes(slug));
      });
    }

    // Filter by footprint
    if (state.selectedFootprints.length > 0) {
      filtered = filtered.filter(p => {
        const footprintSlugs = getEntrySlugs(getProductFootprintEntries(p));
        return state.selectedFootprints.some((slug) => footprintSlugs.includes(slug));
      });
    }

    // Filter by added features
    if (state.selectedAddedFeatures.length > 0) {
      filtered = filtered.filter(p => {
        const addedFeatureSlugs = getEntrySlugs(getProductAddedFeatureEntries(p));
        return state.selectedAddedFeatures.some((slug) => addedFeatureSlugs.includes(slug));
      });
    }

    // Filter by search
    if (state.searchTerm) {
      filtered = filtered.filter(p => fuzzyMatch(p.name, state.searchTerm));
    }

    // Sort
    switch (state.sortBy) {
      case 'price':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'title':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'date_asc':
        filtered.sort((a, b) => a.id - b.id);
        break;
      case 'popularity':
        filtered.sort((a, b) => getProductViewCount(b) - getProductViewCount(a));
        break;
      case 'date':
        filtered.sort((a, b) => b.id - a.id);
        break;
    }

    return filtered;
  }

  // ──────────────────────────────────────────────
  // RENDER PRODUCTS
  // ──────────────────────────────────────────────
  function renderProducts() {
    const grid = $id('nwProductsGrid');
    const noProducts = $id('nw-no-products');
    const loading = $id('nwLoading');
    if (!grid) return;

    if (IS_BEST_SELLERS_COLLECTION && !bestSellersReady) {
      grid.innerHTML = '';
      if (noProducts) noProducts.style.display = 'none';
      if (loading) loading.classList.add('active');
      const countEl = $id('nwResultsCount');
      if (countEl) countEl.textContent = '';
      return;
    }

    if (loading) loading.classList.remove('active');
    const products = getFilteredProducts();

    if (products.length === 0) {
      grid.innerHTML = '';
      if (noProducts) noProducts.style.display = 'block';
      const countEl = $id('nwResultsCount');
      if (countEl) countEl.textContent = '0 products found';
      return;
    }

    if (noProducts) noProducts.style.display = 'none';

    grid.innerHTML = products.map(p => {
      const hasOriginal = SHOW_COMPARE_AT_PRICES && p.originalPrice && p.originalPrice > p.price;
      const formattedPrice = '$' + p.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formattedOriginal = hasOriginal ? '$' + p.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

      const priceHTML = hasOriginal
        ? `<ins>${formattedPrice}</ins> <del>${formattedOriginal}</del>`
        : formattedPrice;

      // Calculate savings for Save badge
      const savings = SHOW_SALE_BADGES && p.originalPrice && p.originalPrice > p.price ? (p.originalPrice - p.price) : 0;
      const formattedSavings = savings > 0
        ? '$' + savings.toLocaleString('en-US', { minimumFractionDigits: (savings % 1 === 0 ? 0 : 2), maximumFractionDigits: 2 })
        : '';
      const saveBadgeHTML = (hasOriginal && savings > 0)
        ? `<span class="nw-sale-badge">Save ${formattedSavings}</span>`
        : '';
      const productId = String(p.productId);
      let rankingBadgeHTML = '';
      if (BEST_SELLER_LABEL && bestSellerProductIds.has(productId)) {
        rankingBadgeHTML = `<span class="nw-sale-badge" data-nw-ranking-badge>${escapeHTML(BEST_SELLER_LABEL)}</span>`;
      } else if (MOST_POPULAR_LABEL && mostPopularProductIds.has(productId)) {
        rankingBadgeHTML = `<span class="nw-sale-badge" data-nw-ranking-badge>${escapeHTML(MOST_POPULAR_LABEL)}</span>`;
      }

      // Stars rating HTML (always rendered, hidden if no reviews to maintain layout height alignment)
      const ratingVal = p.rating ? parseFloat(p.rating) : 0;
      const ratingCountVal = p.ratingCount ? parseInt(p.ratingCount) : 0;
      const hasRating = ratingCountVal > 0 && ratingVal > 0;
      const starsHTML = `
        <div class="nw-product-card-rating${hasRating ? '' : ' nw-rating-placeholder'}"${hasRating ? '' : ' style="visibility: hidden;"'}>
          <div class="nw-stars-outer">
            <div class="nw-stars-inner" style="width: ${hasRating ? ratingVal * 20 : 0}%;"></div>
          </div>
          <span class="nw-rating-count">(${hasRating ? ratingCountVal : 0})</span>
        </div>
      `;

      const sparkParentId = p.productId || '';
      return `
        <div class="nw-product-card" data-id="${p.id}" data-product-id="${p.productId}" data-category="${p.category || ''}" data-price="${p.price}" data-sku="${p.sku || ''}">
          <!-- Full card overlay link for clean click-through across all screens -->
          <a href="${p.href}" class="nw-card-link-overlay" aria-label="${p.name}"></a>
          
          <a href="${p.href}" class="nw-product-media" style="background-image:url('${p.image}')">
            ${rankingBadgeHTML}
            ${saveBadgeHTML}
          </a>
          <div class="nw-product-content">
            <div class="nw-card-title-wrap">
              <a href="${p.href}" class="nw-product-title">${p.name}</a>
              <div class="nw-card-meta-wrap">
                <div class="nw-product-price" data-spark="b2c-only">${priceHTML}</div>
                ${sparkParentId ? `<spark-product-card parent-id="${sparkParentId}"></spark-product-card>` : ''}
                ${starsHTML}
              </div>
            </div>
          </div>
        </div>
      `;
    }).join('');

    if (window.customElements && typeof customElements.upgrade === 'function') {
      grid.querySelectorAll('spark-product-card').forEach((el) => {
        customElements.upgrade(el);
      });
    }

    const countEl = $id('nwResultsCount');
    if (countEl) {
      const total = IS_BEST_SELLERS_COLLECTION
        ? getCollectionProducts().length
        : (TOTAL_PRODUCTS > 0 ? TOTAL_PRODUCTS : PRODUCTS.length);
      countEl.textContent = `Showing ${products.length} of ${total} products`;
    }

    // Bind Add to Cart buttons (Shopify AJAX cart)
    grid.querySelectorAll('.add-to-cart-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = btn.dataset.productId;
        // If Shopify variant ID is available, use AJAX cart
        if (productId && window.Shopify) {
          addToCartAjax(productId, btn);
        } else {
          showToast('Added to cart!');
        }
      });
    });

    alignPriceRatingRowHeights();
  }

  function alignPriceRatingRowHeights() {
    const wraps = document.querySelectorAll('.nw-card-meta-wrap');
    if (!wraps.length) return;

    // Reset heights first to get natural heights
    wraps.forEach(w => {
      w.style.height = 'auto';
    });

    const isMobile = window.innerWidth <= 767;
    const targetHeight = isMobile ? '42px' : '48px';

    // Group elements by their offsetTop
    const rows = {};
    wraps.forEach(w => {
      const card = w.closest('.nw-product-card');
      if (!card) return;
      const top = card.offsetTop;
      if (!rows[top]) {
        rows[top] = [];
      }
      rows[top].push(w);
    });

    // Align heights within each row group
    for (const top in rows) {
      const rowWraps = rows[top];
      // Check if any wrap in this row has a visible rating
      const hasAnyRating = rowWraps.some(w => {
        const rating = w.querySelector('.nw-product-card-rating');
        return rating && !rating.classList.contains('nw-rating-placeholder');
      });

      if (hasAnyRating) {
        rowWraps.forEach(w => {
          w.style.height = targetHeight;
        });
      } else {
        rowWraps.forEach(w => {
          w.style.height = 'auto';
        });
      }
    }
  }

  // ──────────────────────────────────────────────
  // FETCH REMAINING PRODUCTS (pages 2+)
  // Uses Shopify's /collections/{handle}/products.json API.
  // Products fetched this way won't carry metafield filter data (Shopify API
  // limitation), but they will appear in the grid when no metafield filter
  // is active. The first 250 loaded via Liquid retain full filter support.
  // ──────────────────────────────────────────────
  function mapApiProduct(p) {
    const variant = (p.variants && p.variants[0]) || {};
    const price = variant.price ? parseFloat(variant.price) : 0;
    const comparePrice = variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;
    return {
      id: variant.id || p.id,
      productId: p.id,
      name: p.title || '',
      vendor: p.vendor || '',
      typeName: p.product_type || '',
      filterSizeMeta: null,
      filterTypeMeta: null,
      filterBrandMeta: null,
      filterFeaturesMeta: null,
      filterCircuitsMeta: null,
      filterFootprintMeta: null,
      filterAddedFeaturesMeta: null,
      price: price,
      originalPrice: comparePrice,
      category: (p.tags && p.tags[0]) || '',
      categorySlugs: (p.tags || []).map((t) => normalizeToSlug(t)),
      categories: p.tags || [],
      type: normalizeToSlug(p.product_type || ''),
      types: [normalizeToSlug(p.product_type || '')],
      sku: variant.sku || '',
      image: (p.images && p.images[0] && p.images[0].src) || null,
      href: '/products/' + p.handle,
      hasAddons: false,
      isPreOrder: false,
      rating: 0,
      ratingCount: 0
    };
  }

  function getCollectionUrl(pathSuffix = '') {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    const normalizedRoot = root.endsWith('/') ? root : root + '/';
    return new URL(
      `${normalizedRoot}collections/${encodeURIComponent(COLLECTION_HANDLE)}${pathSuffix}`,
      window.location.origin
    );
  }

  function buildRankingsUrl(page) {
    const url = getCollectionUrl();
    url.searchParams.set('view', VIEW_RANKINGS_VIEW);
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  async function fetchRankingsPage(page) {
    const response = await fetch(buildRankingsUrl(page));
    if (!response.ok) throw new Error('Unable to load collection rankings');
    const data = await response.json();
    if (!data || !Array.isArray(data.products)) {
      throw new Error('Invalid collection rankings response');
    }
    if (data.collectionHandle !== COLLECTION_HANDLE || data.sortBy !== 'best-selling') {
      throw new Error('Collection rankings were not sorted by best-selling');
    }
    return data;
  }

  async function fetchCollectionRankings() {
    if (!COLLECTION_HANDLE) return;

    let firstPage;
    try {
      firstPage = await fetchRankingsPage(1);
      bestSellingProductIds = firstPage.products
        .filter((product) => product && product.id)
        .map((product) => String(product.id));
      bestSellerProductIds = new Set(bestSellingProductIds.slice(0, 3));
    } catch (error) {
      bestSellingProductIds = [];
      bestSellerProductIds = new Set();
      mostPopularProductIds = new Set();
      productViewCounts = new Map();
      console.warn('[NW Shop] Collection rankings could not be loaded.', error);
      return;
    }

    try {
      const totalPages = Math.max(1, Number(firstPage.totalPages) || 1);
      const pageRequests = [];
      for (let page = 2; page <= totalPages; page++) {
        pageRequests.push(fetchRankingsPage(page));
      }
      const remainingPages = await Promise.all(pageRequests);
      const rankingProducts = [firstPage, ...remainingPages]
        .flatMap((page) => page.products)
        .filter((product) => product && product.id);
      bestSellingProductIds = rankingProducts.map((product) => String(product.id));

      productViewCounts = new Map();
      const bestSellingPositions = new Map();
      bestSellingProductIds.forEach((productId, index) => {
        bestSellingPositions.set(productId, index);
      });

      const productsWithViewCounts = rankingProducts
        .filter((product) => product.hasViewCount === true)
        .map((product, index) => {
          const productId = String(product.id);
          const viewCount = Number(product.viewCount) || 0;
          productViewCounts.set(productId, viewCount);
          return { productId, viewCount, index };
        });

      productsWithViewCounts.sort((a, b) => {
        const countDifference = b.viewCount - a.viewCount;
        if (countDifference !== 0) return countDifference;
        const aSalesPosition = bestSellingPositions.get(a.productId) ?? Number.MAX_SAFE_INTEGER;
        const bSalesPosition = bestSellingPositions.get(b.productId) ?? Number.MAX_SAFE_INTEGER;
        return aSalesPosition - bSalesPosition || a.index - b.index;
      });

      mostPopularProductIds = new Set(
        productsWithViewCounts.slice(0, 3).map((product) => product.productId)
      );
    } catch (error) {
      mostPopularProductIds = new Set();
      productViewCounts = new Map();
      console.warn('[NW Shop] View rankings could not be loaded.', error);
    }
  }

  async function fetchRemainingProducts() {
    if (!COLLECTION_HANDLE || TOTAL_PAGES <= 1) return;

    const existingIds = new Set(PRODUCTS.map((p) => String(p.productId)));

    for (let page = 2; page <= TOTAL_PAGES; page++) {
      try {
        const url = `/collections/${COLLECTION_HANDLE}/products.json?limit=250&page=${page}`;
        const res = await fetch(url);
        if (!res.ok) break;
        const data = await res.json();
        const newProds = (data.products || []).map(mapApiProduct);
        let added = false;
        newProds.forEach((p) => {
          const productId = String(p.productId);
          if (!existingIds.has(productId)) {
            PRODUCTS.push(p);
            existingIds.add(productId);
            added = true;
          }
        });
        if (added && !IS_BEST_SELLERS_COLLECTION) renderProducts();
      } catch (e) {
        break;
      }
    }
  }

  // ──────────────────────────────────────────────
  // SHOPIFY AJAX ADD TO CART
  // ──────────────────────────────────────────────
  function addToCartAjax(variantId, btn) {
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';
    btn.style.pointerEvents = 'none';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
    })
    .then(res => {
      if (!res.ok) throw new Error('Failed to add');
      return res.json();
    })
    .then(() => {
      showToast('Added to cart!');
      btn.textContent = 'Added ✓';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.style.pointerEvents = '';
      }, 1500);
      // Dispatch events for cart drawer / icon update and mini-cart open
      fetch('/cart.js')
        .then(res => res.json())
        .then(cartData => {
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: { data: { itemCount: cartData.item_count } }
          }));
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:item-added'));
        })
        .catch(() => {
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:item-added'));
        });
    })
    .catch(() => {
      showToast('Could not add to cart');
      btn.textContent = originalText;
      btn.style.pointerEvents = '';
    });
  }

  // ──────────────────────────────────────────────
  // UPDATE UI
  // ──────────────────────────────────────────────
  function updateUI() {
    const totalFilters =
      state.selectedCategories.length +
      state.selectedSizes.length +
      state.selectedTypes.length +
      state.selectedBrands.length +
      state.selectedFeatures.length +
      state.selectedCircuits.length +
      state.selectedFootprints.length +
      state.selectedAddedFeatures.length +
      (state.minPrice !== null ? 1 : 0) +
      (state.maxPrice !== null ? 1 : 0) +
      (state.searchTerm ? 1 : 0);
    const hasFilters = totalFilters > 0;

    // Active filters bar
    const filtersBar = $id('nw-active-filters');
    if (filtersBar) {
      if (hasFilters) {
        filtersBar.classList.add('visible');
      } else {
        filtersBar.classList.remove('visible');
      }
    }

    // Filter count badge
    const badge = $id('nwFilterCount');
    if (badge) {
      if (totalFilters > 0) {
        badge.textContent = totalFilters;
        badge.classList.add('visible');
      } else {
        badge.classList.remove('visible');
      }
    }

    // Build filter tags
    const tagsContainer = $id('nw-filter-tags');
    if (tagsContainer) {
      let tagsHTML = '';

      state.selectedCategories.forEach(slug => {
        const c = CATEGORY_OPTIONS.find(x => x.slug === slug);
        const label = c ? c.name : slug.replace(/-/g, ' ').replace(/\b\w/g, d => d.toUpperCase());
        tagsHTML += buildTagHTML('category', slug, label);
      });

      state.selectedSizes.forEach(slug => {
        const size = SIZE_OPTIONS.find(x => x.slug === slug);
        const label = size ? size.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('size', slug, label);
      });

      state.selectedTypes.forEach(slug => {
        const t = TYPE_OPTIONS.find(x => x.slug === slug);
        const label = t ? t.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('type', slug, label);
      });

      state.selectedBrands.forEach(slug => {
        const b = BRAND_OPTIONS.find(x => x.slug === slug);
        const label = b ? b.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('brand', slug, label);
      });

      state.selectedFeatures.forEach(slug => {
        const f = FEATURE_OPTIONS.find(x => x.slug === slug);
        const label = f ? f.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('feature', slug, label);
      });

      state.selectedCircuits.forEach(slug => {
        const c = CIRCUIT_OPTIONS.find(x => x.slug === slug);
        const label = c ? c.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('circuit', slug, label);
      });

      state.selectedFootprints.forEach(slug => {
        const f = FOOTPRINT_OPTIONS.find(x => x.slug === slug);
        const label = f ? f.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('footprint', slug, label);
      });

      state.selectedAddedFeatures.forEach(slug => {
        const f = ADDED_FEATURE_OPTIONS.find(x => x.slug === slug);
        const label = f ? f.name : slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        tagsHTML += buildTagHTML('added-feature', slug, label);
      });

      if (state.searchTerm) {
        tagsHTML += buildTagHTML('search', '', 'Search: "' + state.searchTerm + '"');
      }

      tagsContainer.innerHTML = tagsHTML;
    }

    updateURL();
  }

  function buildTagHTML(type, value, label) {
    return `
      <span class="nw-filter-tag">
        ${label}
        <span class="remove-tag" onclick="NWShop.removeFilterTag('${type}', '${value}')">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M1 1l12 12M13 1L1 13"/>
          </svg>
        </span>
      </span>
    `;
  }

  // ──────────────────────────────────────────────
  // URL STATE
  // ──────────────────────────────────────────────
  // Query parameters this page owns. Every other param in the URL (utm_*, gclid,
  // country, currency, ...) is left untouched so marketing attribution survives a
  // filter interaction — rebuilding the query string from scratch used to drop it,
  // which made tagged visits look like Direct traffic to client-side analytics.
  const OWNED_PARAMS = [
    'category',
    'size',
    'type',
    'brand',
    'feature',
    'circuit',
    'footprint',
    'added_feature',
    'min_price',
    'max_price',
    'search',
    'sort',
  ];

  function updateURL() {
    const url = new URL(window.location.href);

    OWNED_PARAMS.forEach(key => url.searchParams.delete(key));

    const params = url.searchParams;
    if (state.selectedCategories.length) params.set('category', state.selectedCategories.join(','));
    if (state.selectedSizes.length) params.set('size', state.selectedSizes.join(','));
    if (state.selectedTypes.length) params.set('type', state.selectedTypes.join(','));
    if (state.selectedBrands.length) params.set('brand', state.selectedBrands.join(','));
    if (state.selectedFeatures.length) params.set('feature', state.selectedFeatures.join(','));
    if (state.selectedCircuits.length) params.set('circuit', state.selectedCircuits.join(','));
    if (state.selectedFootprints.length) params.set('footprint', state.selectedFootprints.join(','));
    if (state.selectedAddedFeatures.length) params.set('added_feature', state.selectedAddedFeatures.join(','));
    if (state.minPrice !== null) params.set('min_price', String(state.minPrice));
    if (state.maxPrice !== null) params.set('max_price', String(state.maxPrice));
    if (state.searchTerm) params.set('search', state.searchTerm);
    if (state.sortBy) params.set('sort', state.sortBy);

    history.replaceState(null, '', url.pathname + url.search + url.hash);
  }

  function restoreFromURL() {
    const params = new URLSearchParams(window.location.search);

    if (params.has('category')) {
      state.selectedCategories = params.get('category').split(',').filter(Boolean);
    }

    if (params.has('size')) {
      state.selectedSizes = params.get('size').split(',').filter(Boolean);
    }
    if (params.has('type')) {
      state.selectedTypes = params.get('type').split(',').filter(Boolean);
    }
    if (params.has('brand')) {
      state.selectedBrands = params.get('brand').split(',').filter(Boolean);
    }
    if (params.has('feature')) {
      state.selectedFeatures = params.get('feature').split(',').filter(Boolean);
    }
    if (params.has('circuit')) {
      state.selectedCircuits = params.get('circuit').split(',').filter(Boolean);
    }
    if (params.has('footprint')) {
      state.selectedFootprints = params.get('footprint').split(',').filter(Boolean);
    }
    if (params.has('added_feature')) {
      state.selectedAddedFeatures = params.get('added_feature').split(',').filter(Boolean);
    }
    if (params.has('min_price')) {
      const minPrice = Number(params.get('min_price'));
      state.minPrice = Number.isFinite(minPrice) ? minPrice : null;
      const minPriceEl = $id('nwPriceMin');
      if (minPriceEl && state.minPrice !== null) minPriceEl.value = String(state.minPrice);
    }
    if (params.has('max_price')) {
      const maxPrice = Number(params.get('max_price'));
      state.maxPrice = Number.isFinite(maxPrice) ? maxPrice : null;
      const maxPriceEl = $id('nwPriceMax');
      if (maxPriceEl && state.maxPrice !== null) maxPriceEl.value = String(state.maxPrice);
    }
    if (params.has('search')) {
      state.searchTerm = params.get('search');
      const searchEl = $id('nw-search');
      if (searchEl) searchEl.value = state.searchTerm;
    }
    if (params.has('sort')) {
      state.sortBy = params.get('sort');
      const sortEl = $id('nw-sort');
      if (sortEl) sortEl.value = state.sortBy;
    }

    state.selectedCategories = sanitizeSelectedValues(state.selectedCategories, CATEGORY_OPTIONS);

    // Scopes the sidebar to ?category= and sanitizes the other selections against it.
    refreshFacetScope();
  }

  function sanitizeSelectedValues(selectedValues, options) {
    const allowed = new Set(options.map((option) => option.slug));
    return (selectedValues || []).filter((value) => allowed.has(value));
  }

  // ──────────────────────────────────────────────
  // TOAST NOTIFICATION
  // ──────────────────────────────────────────────
  function showToast(msg) {
    const toast = $id('nwToast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('active');
    setTimeout(() => toast.classList.remove('active'), 2500);
  }

  // ──────────────────────────────────────────────
  // BOOT
  // ──────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('#nwProductsGrid')) {
      init();
    }
  });

  // ──────────────────────────────────────────────
  // PUBLIC API
  // ──────────────────────────────────────────────
  return {
    removeFilterTag,
    showToast,
    openSidebar,
    closeSidebar,
  };
})();
